import React from 'react';
import { css } from '@compiled/react';

import { cssMap, cx } from '@atlaskit/css';
import AiAgentIcon from '@atlaskit/icon/core/ai-agent';
import IconTile from '@atlaskit/icon/icon-tile';
import { Box } from '@atlaskit/primitives/compiled/box';
import { AgentAvatar } from '@atlaskit/rovo-agent-components';
import { token } from '@atlaskit/tokens';

import { isForgeAgent } from '@atlassian/conversation-assistant-agent';

import { fg } from '@confluence/feature-gating';

import { getParticipantColors } from '../presenceUtils.ts';

import type { AgentWithPresence } from './AgentPresenceTypes.ts';

const AGENT_AVATAR_CLIP_PATH = `polygon(45% 1.33975%, 46.5798% 0.60307%, 48.26352% 0.15192%, 50% 0%, 51.73648% 0.15192%, 53.4202% 0.60307%, 55% 1.33975%, 89.64102% 21.33975%, 91.06889% 22.33956%, 92.30146% 23.57212%, 93.30127% 25%, 94.03794% 26.5798%, 94.48909% 28.26352%, 94.64102% 30%, 94.64102% 70%, 94.48909% 71.73648%, 94.03794% 73.4202%, 93.30127% 75%, 92.30146% 76.42788%, 91.06889% 77.66044%, 89.64102% 78.66025%, 55% 98.66025%, 53.4202% 99.39693%, 51.73648% 99.84808%, 50% 100%, 48.26352% 99.84808%, 46.5798% 99.39693%, 45% 98.66025%, 10.35898% 78.66025%, 8.93111% 77.66044%, 7.69854% 76.42788%, 6.69873% 75%, 5.96206% 73.4202%, 5.51091% 71.73648%, 5.35898% 70%, 5.35898% 30%, 5.51091% 28.26352%, 5.96206% 26.5798%, 6.69873% 25%, 7.69854% 23.57212%, 8.93111% 22.33956%, 10.35898% 21.33975%)`;
const AGENT_AVATAR_BORDER_WIDTH = 2;
const AGENT_AVATAR_SIZE_PX = {
	small: 24,
	medium: 32,
};

const externalAgentAvatarBackgroundStyles = css({
	display: 'inline-flex',
	// Match the hexagonal shape used by AgentAvatar in Studio.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	clipPath: AGENT_AVATAR_CLIP_PATH,
	// Match the neutral background used by the unidentified external-agent avatar in Analytics.
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage, @atlaskit/design-system/no-unsafe-design-token-usage
	'--ds-background-neutral': '#DDDEE1',
});

const styles = cssMap({
	root: {
		display: 'inline-flex',
		flexShrink: 0,
		position: 'relative',
	},
	overlapped: {
		marginInlineStart: token('space.negative.075'),
	},
	border: {
		display: 'flex',
		flexShrink: 0,
		alignItems: 'center',
		justifyContent: 'center',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		clipPath: AGENT_AVATAR_CLIP_PATH,
	},
	badge: {
		position: 'absolute',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: '12px',
		width: '12px',
		borderRadius: token('radius.small'),
		fontWeight: token('font.weight.semibold'),
	},
	legacyBorder: {
		position: 'relative',
		filter: `drop-shadow(1px 0px 0px ${token('color.icon.inverse')}) drop-shadow(-1px 0px 0px ${token('color.icon.inverse')}) drop-shadow(0px 1px 0px ${token('color.icon.inverse')}) drop-shadow(0px -1px 0px ${token('color.icon.inverse')})`,
	},
});

const ExternalAgentAvatar = ({ size }: { size: 'small' | 'medium' }): React.JSX.Element => (
	<span data-testid="external-agent-avatar" css={externalAgentAvatarBackgroundStyles}>
		<IconTile icon={AiAgentIcon} label="" appearance="gray" size={size} />
	</span>
);

export type AgentPresenceAvatarProps = {
	agent: AgentWithPresence;
	presenceColorKey?: string;
	isOverlapped?: boolean;
	stackIndex?: number;
	showPresenceBadge?: boolean;
	showBorder?: boolean;
	size?: 'small' | 'medium';
};

export function AgentPresenceAvatar({
	agent,
	presenceColorKey,
	isOverlapped,
	stackIndex,
	showPresenceBadge,
	showBorder,
	size = 'medium',
}: AgentPresenceAvatarProps): React.JSX.Element {
	const renderAgentAvatar = (avatarSize: 'small' | 'medium') =>
		agent.isExternalAgentFallback ? (
			<ExternalAgentAvatar size={avatarSize} />
		) : (
			<AgentAvatar
				key={agent.id}
				imageUrl={agent.avatarUrl}
				agentId={agent.id}
				agentIdentityAccountId={agent.identity_account_id}
				agentNamedId={agent.external_config_reference ?? agent.named_id}
				size={avatarSize}
				showBorder={false}
				isForgeAgent={isForgeAgent(agent)}
				forgeAgentIconUrl={agent.icon}
			/>
		);

	if (!fg('platform_move_presence_agents')) {
		return (
			<Box
				xcss={cx(showBorder && styles.legacyBorder)}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Restores the legacy runtime z-index used by the standalone agent avatar group.
				style={{ zIndex: stackIndex }}
			>
				{renderAgentAvatar('medium')}
			</Box>
		);
	}

	const colors = getParticipantColors(presenceColorKey ?? agent.id);
	const presenceColorStyles = showPresenceBadge
		? ({
				'--agent-presence-border-color': colors.backgroundColor,
				'--agent-presence-badge-background': colors.backgroundColor,
				'--agent-presence-badge-color': colors.textColor,
			} as React.CSSProperties)
		: undefined;
	const rootStyle: React.CSSProperties = {
		zIndex: stackIndex,
		...presenceColorStyles,
	};
	const borderedAvatarSize = AGENT_AVATAR_SIZE_PX[size] + AGENT_AVATAR_BORDER_WIDTH * 2;
	const shouldShowPresenceBadge = showPresenceBadge && agent.presenceActivity === 'editor';
	const borderStyle: React.CSSProperties = {
		backgroundColor: `var(--agent-presence-border-color, ${token('color.icon.inverse')})`,
		height: borderedAvatarSize,
		width: borderedAvatarSize,
	};
	const badgeStyle: React.CSSProperties = {
		backgroundColor: 'var(--agent-presence-badge-background)',
		bottom: '-2px',
		color: 'var(--agent-presence-badge-color)',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: 10,
		right: '-2px',
		userSelect: 'none',
		zIndex: 2,
	};

	const agentAvatar = renderAgentAvatar(size);

	return (
		<Box
			xcss={cx(showBorder && styles.root, isOverlapped && styles.overlapped)}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Uses runtime z-index and presence color values from participant color assignment.
			style={rootStyle}
			testId="agent-presence-avatar"
		>
			{showBorder ? (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Uses runtime dimensions to match the selected AgentAvatar size plus the presence border.
				<Box xcss={styles.border} style={borderStyle}>
					{agentAvatar}
				</Box>
			) : (
				agentAvatar
			)}
			{shouldShowPresenceBadge && (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Uses runtime presence colors and badge offsets that align with the overlapped avatar.
				<Box as="span" xcss={styles.badge} style={badgeStyle} testId="agent-presence-avatar-badge">
					{agent.name?.substring(0, 1)}
				</Box>
			)}
		</Box>
	);
}
